/**
 * Iter 100 — Drop Rates editor for the Admin panel.
 *
 * Loads the effective drop rates from /api/admin/config/drop-rates,
 * lets the admin tune every probability via numeric inputs (we use raw
 * % values 0-100 in the UI; we convert to 0.0-1.0 floats on save), and
 * PUTs the result back. Reset button restores defaults.
 *
 * Sections:
 *  1. Ally drop (boss / normal)
 *  2. Foil chance (battle drop + per-rarity crafting)
 *  3. Dust multiplier (slider 0.5x - 3.0x)
 *  4. Per-pack rarity weights + foil chance (auto-normalize on save)
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RotateCcw, Save, Sparkles, Coins, Gem, Package, Loader2 } from "lucide-react";

const RARITIES = ["common", "uncommon", "rare", "epic", "legendary"];
const PACK_IDS = ["silver", "gold", "prismatic"];

const RARITY_COLOR = {
  common:    "text-slate-300",
  uncommon:  "text-emerald-300",
  rare:      "text-sky-300",
  epic:      "text-purple-300",
  legendary: "text-amber-300",
};

const RARITY_LABEL_IT = {
  common: "Comune",
  uncommon: "Non comune",
  rare: "Raro",
  epic: "Epico",
  legendary: "Leggendario",
};

const PACK_LABEL_IT = {
  silver: "Argenteo (Silver)",
  gold: "Aureo (Gold)",
  prismatic: "Prismatico (Prismatic)",
};

// percentage I/O helpers -------------------------------------------------
const toPct = (v) => (Number.isFinite(v) ? +(v * 100).toFixed(2) : 0);
const toUnit = (v) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n)) / 100;
};

function PctInput({ value, onChange, testId, suffix = "%", min = 0, max = 100, step = 0.5 }) {
  return (
    <div className="flex items-center gap-1 w-full max-w-[110px]">
      <Input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0] text-sm h-8 px-2 py-0"
      />
      <span className="text-[10px] uppercase tracking-wider text-[#94a3b8]">{suffix}</span>
    </div>
  );
}

export default function DropRatesTab() {
  const { token, API } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Local form state (uses % strings to avoid focus-loss while typing)
  const [allyBoss, setAllyBoss] = useState("5");
  const [allyNormal, setAllyNormal] = useState("15");
  const [foilBattle, setFoilBattle] = useState("2");
  const [foilCrafting, setFoilCrafting] = useState({ common: "5", uncommon: "5", rare: "15", epic: "25", legendary: "50" });
  const [dustMult, setDustMult] = useState("1.0");
  const [packs, setPacks] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/config/drop-rates`, { headers });
      const d = r.data || {};
      setData(d);
      // Pre-fill form
      setAllyBoss(String(toPct(d.ally?.boss_chance ?? 0.05)));
      setAllyNormal(String(toPct(d.ally?.normal_chance ?? 0.15)));
      setFoilBattle(String(toPct(d.foil?.battle_drop ?? 0.02)));
      const fc = d.foil?.crafting || {};
      setFoilCrafting({
        common:   String(toPct(fc.common   ?? 0.05)),
        uncommon: String(toPct(fc.uncommon ?? 0.05)),
        rare:     String(toPct(fc.rare     ?? 0.15)),
        epic:     String(toPct(fc.epic     ?? 0.25)),
        legendary:String(toPct(fc.legendary?? 0.5)),
      });
      setDustMult(String(d.dust?.global_multiplier ?? 1.0));
      const packsObj = {};
      for (const pid of PACK_IDS) {
        const p = d.packs?.[pid] || {};
        packsObj[pid] = {
          foil_chance: String(toPct(p.foil_chance ?? 0.15)),
          rarity_weights: {
            common:    String(toPct(p.rarity_weights?.common    ?? 0.6)),
            uncommon:  String(toPct(p.rarity_weights?.uncommon  ?? 0.25)),
            rare:      String(toPct(p.rarity_weights?.rare      ?? 0.1)),
            epic:      String(toPct(p.rarity_weights?.epic      ?? 0.04)),
            legendary: String(toPct(p.rarity_weights?.legendary ?? 0.01)),
          },
        };
      }
      setPacks(packsObj);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento drop rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  // Sum of rarity weights per pack (to show the admin if normalization will trigger)
  const packWeightSums = useMemo(() => {
    const out = {};
    for (const pid of PACK_IDS) {
      const w = packs[pid]?.rarity_weights || {};
      out[pid] = RARITIES.reduce((s, r) => s + (parseFloat(w[r]) || 0), 0);
    }
    return out;
  }, [packs]);

  const setAlly = (which, v) => {
    if (which === "boss") setAllyBoss(v);
    else setAllyNormal(v);
    setDirty(true);
  };
  const setFoilCraftingFor = (rarity, v) => {
    setFoilCrafting((prev) => ({ ...prev, [rarity]: v }));
    setDirty(true);
  };
  const setPackFoil = (pid, v) => {
    setPacks((prev) => ({ ...prev, [pid]: { ...prev[pid], foil_chance: v } }));
    setDirty(true);
  };
  const setPackWeight = (pid, rarity, v) => {
    setPacks((prev) => ({
      ...prev,
      [pid]: {
        ...prev[pid],
        rarity_weights: { ...prev[pid].rarity_weights, [rarity]: v },
      },
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ally: { boss_chance: toUnit(allyBoss), normal_chance: toUnit(allyNormal) },
        foil: {
          battle_drop: toUnit(foilBattle),
          crafting: Object.fromEntries(RARITIES.map((r) => [r, toUnit(foilCrafting[r])])),
        },
        dust: { global_multiplier: Math.max(0.0, Math.min(10.0, parseFloat(dustMult) || 1.0)) },
        packs: Object.fromEntries(PACK_IDS.map((pid) => [pid, {
          foil_chance: toUnit(packs[pid].foil_chance),
          rarity_weights: Object.fromEntries(RARITIES.map((r) => [r, toUnit(packs[pid].rarity_weights[r])])),
        }])),
      };
      const r = await axios.put(`${API}/admin/config/drop-rates`, payload, { headers });
      toast.success("Drop rates aggiornati ✨");
      setData(r.data);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare TUTTI i drop rate ai valori di default? Le tue modifiche andranno perse."))
      return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/config/drop-rates/reset`, {}, { headers });
      toast.success("Drop rates ripristinati ai default");
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore reset");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#94a3b8]">
        <Loader2 className="animate-spin mr-2" size={20} /> Carico configurazione...
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="drop-rates-tab">
      {/* Action bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl text-[#cfb53b]">Drop Rates</h2>
          <p className="text-xs text-[#94a3b8] mt-0.5">Modifica le probabilità di drop di carte alleato, foil, polvere e pacchetti. I cambiamenti sono LIVE in pochi secondi.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={reset}
            disabled={saving}
            data-testid="drop-rates-reset"
            className="border-[#475569]/40 text-[#94a3b8] hover:bg-[#1a1a1d] text-xs"
          >
            <RotateCcw size={14} className="mr-1.5" /> Reset default
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="drop-rates-save"
            className="bg-[#cfb53b] text-[#0a0a0c] hover:bg-[#e5cc5b] disabled:opacity-50 text-xs"
          >
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
            Salva modifiche
          </Button>
        </div>
      </div>
      {dirty && (
        <div className="text-xs text-[#cfb53b] bg-[#cfb53b]/5 border border-[#cfb53b]/30 rounded px-3 py-1.5">
          🟡 Hai modifiche non salvate.
        </div>
      )}

      {/* Ally Drop */}
      <section className="bg-[#1a1a1d]/80 border border-[#cfb53b]/15 rounded-md p-4 space-y-3">
        <h3 className="font-heading text-base text-[#e2e8f0] flex items-center gap-2">
          <Sparkles size={16} className="text-[#cfb53b]" /> Drop Carte Alleato
        </h3>
        <p className="text-[11px] text-[#94a3b8]">Probabilità che il nemico lasci cadere una carta alleato dopo la vittoria in battaglia.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[#94a3b8]">Boss</Label>
            <PctInput value={allyBoss} onChange={(v) => setAlly("boss", v)} testId="ally-boss-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[#94a3b8]">Nemici normali</Label>
            <PctInput value={allyNormal} onChange={(v) => setAlly("normal", v)} testId="ally-normal-input" />
          </div>
        </div>
      </section>

      {/* Foil chance */}
      <section className="bg-[#1a1a1d]/80 border border-[#cfb53b]/15 rounded-md p-4 space-y-3">
        <h3 className="font-heading text-base text-[#e2e8f0] flex items-center gap-2">
          <Gem size={16} className="text-purple-300" /> Probabilità Foil
        </h3>
        <p className="text-[11px] text-[#94a3b8]">Carte con effetto luccicante. Più rare delle versioni normali.</p>
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[#94a3b8]">Drop di battaglia (alleati)</Label>
            <PctInput value={foilBattle} onChange={(v) => { setFoilBattle(v); setDirty(true); }} testId="foil-battle-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#94a3b8] block mb-1.5">Crafting per rarità</Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {RARITIES.map((r) => (
                <div key={r} className="space-y-1">
                  <div className={`text-[10px] ${RARITY_COLOR[r]} uppercase`}>{RARITY_LABEL_IT[r]}</div>
                  <PctInput
                    value={foilCrafting[r]}
                    onChange={(v) => setFoilCraftingFor(r, v)}
                    testId={`foil-crafting-${r}-input`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dust multiplier */}
      <section className="bg-[#1a1a1d]/80 border border-[#cfb53b]/15 rounded-md p-4 space-y-3">
        <h3 className="font-heading text-base text-[#e2e8f0] flex items-center gap-2">
          <Coins size={16} className="text-amber-300" /> Moltiplicatore Polvere Arcana
        </h3>
        <p className="text-[11px] text-[#94a3b8]">Applicato globalmente sopra ai range per nemico. <span className="text-[#cfb53b]">1.0</span> = nessun cambio, <span className="text-[#cfb53b]">2.0</span> = doppia polvere, etc. Range consentito: 0.0 - 10.0.</p>
        <div className="flex items-center gap-2 max-w-[180px]">
          <Input
            type="number"
            min="0" max="10" step="0.1"
            value={dustMult}
            onChange={(e) => { setDustMult(e.target.value); setDirty(true); }}
            data-testid="dust-multiplier-input"
            className="bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0] text-sm h-8"
          />
          <span className="text-[10px] uppercase tracking-wider text-[#94a3b8]">×</span>
        </div>
      </section>

      {/* Packs */}
      <section className="bg-[#1a1a1d]/80 border border-[#cfb53b]/15 rounded-md p-4 space-y-4">
        <h3 className="font-heading text-base text-[#e2e8f0] flex items-center gap-2">
          <Package size={16} className="text-sky-300" /> Pacchetti dello Shop
        </h3>
        <p className="text-[11px] text-[#94a3b8]">Pesi rarità + chance foil per ogni tipo di pacchetto. I pesi vengono auto-normalizzati al salvataggio (la loro somma diventa 100%).</p>
        {PACK_IDS.map((pid) => {
          const p = packs[pid];
          if (!p) return null;
          const sum = packWeightSums[pid] || 0;
          return (
            <div key={pid} className="border border-[#475569]/30 rounded p-3 space-y-2" data-testid={`pack-row-${pid}`}>
              <div className="flex items-center justify-between">
                <h4 className="font-heading text-sm text-[#cfb53b]">{PACK_LABEL_IT[pid]}</h4>
                <div className="text-[10px] text-[#94a3b8]">
                  Somma pesi: <span className={Math.abs(sum - 100) > 0.5 ? "text-orange-400" : "text-emerald-400"}>{sum.toFixed(1)}%</span>
                  {Math.abs(sum - 100) > 0.5 && <span className="ml-1 text-orange-400">(normalizzato a 100% al save)</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                {RARITIES.map((r) => (
                  <div key={r} className="space-y-1">
                    <div className={`text-[10px] ${RARITY_COLOR[r]} uppercase`}>{RARITY_LABEL_IT[r]}</div>
                    <PctInput
                      value={p.rarity_weights[r]}
                      onChange={(v) => setPackWeight(pid, r, v)}
                      testId={`pack-${pid}-weight-${r}-input`}
                    />
                  </div>
                ))}
                <div className="space-y-1">
                  <div className="text-[10px] text-purple-300 uppercase">Foil</div>
                  <PctInput
                    value={p.foil_chance}
                    onChange={(v) => setPackFoil(pid, v)}
                    testId={`pack-${pid}-foil-input`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
